import { ProjectParameters } from 'ducks/types/store-types';
import { JobDefinitionBaseRow } from 'ducks/types/table-types';
import { NullableInterface, UnionFromArray } from 'ducks/types/type-helpers';

export type AdminOptionsTrigger = 'reset' | 'update' | 'unlock' | 'lock' | 'edit';
export interface AdminOptions {
  name: string;
  trigger: AdminOptionsTrigger;
  user_id?: number;
  uuid: number;
}

type CardOptionTrigger = 'edit' | 'delete';
export interface CardOptions {
  name: string;
  trigger: CardOptionTrigger;
  icon: string;
  uuid: number;
}

type BatchDefinitionOptionTrigger = 'run' | 'schedule' | 'unarchive' | 'archive';
export interface BatchDefinitionOption {
  name: string;
  trigger: BatchDefinitionOptionTrigger;
  uuid: number;
}

export interface BatchDefinitionItem extends BatchDefinitionOption {
  project_id: number;
  batch_definition_id: number;
  scheduled_batch_id: number;
}

export interface ToolbarType {
  icon: string[];
  uuid: number;
  name: string;
  label: string;
  focus: string | number | boolean;
}

export interface JobTab {
  name: 'STDOUT' | 'Inputs' | 'Outputs' | 'History' | string;
  active: number;
  uuid: number;
}

export interface InputState {
  float_value: number;
  formatted_value: string;
  value: string | number;
}

export interface JobDefinitionActionItem {
  row: JobDefinitionBaseRow;
  name: string;
  trigger: 'edit' | 'delete';
  uuid: number;
}

type JobDefinitionTrigger = 'run' | 'unarchive' | 'archive';
export interface JobDefinitionsOption {
  name: string;
  trigger: JobDefinitionTrigger;
  uuid: number;
}

export interface JobDefinitionsItem extends JobDefinitionsOption {
  project_id: number;
  job_definition_id: number;
}

export type JobsTrigger = 'run' | 'edit' | 'stop';
export interface JobsItem {
  name: string;
  trigger: JobsTrigger;
  uuid: number;
  project_id: number;
  job_definition_id: number;
  job_id: number;
}

export type ScheduledBatchesTrigger = 'run' | 'edit' | 'stop' | 'start' | 'history' | 'archive' | 'unarchive';
export interface ScheduledBatchesItem {
  name: string;
  trigger: ScheduledBatchesTrigger;
  uuid: number;
  project_id: number;
  batch_definition_id: number;
  scheduled_batch_id: number;
}

export type AdvancedFilterFormState = NullableInterface<{
  start_date: string;
  range_name: string;
  finish_date: string;
  include_archived: boolean;
  parameter_name: string;
  parameter_value: string;
  created_by: boolean;
  max_rows: number;
  job_state: string;
  refresh_timeout: string;
  min_refresh: number;
}>;

export interface CardInfoParams {
  parameter_name: string;
}

export interface CardInfo {
  current_params: CardInfoParams[];
  children_params: CardInfoParams[];
  batch_definition_item_id: number | null;
  children_item_ids: number[];
  batch_definition_item_name: string;
}

export type ParametersTrigger = 'edit' | 'delete';

export interface ParametersOption {
  name: string;
  trigger: ParametersTrigger;
  uuid: number;
}
export interface ParametersTableItem extends ParametersOption {
  row: UnionFromArray<ProjectParameters>;
}

export type ProjectParameter = UnionFromArray<ProjectParameters>;

export interface ProjectFormState {
  project_name: string;
}

export interface JobDates {
  range_name: string;
  start_date: string | null;
  finish_date: string | null;
  uuid: string;
}

export type ExpandedTab = JobTab;

export interface JobDefinitionCellOption {
  name: string;
  trigger: 'edit' | 'delete';
  uuid: number;
}

export interface JobDefinitionsCellOption {
  name: string;
  trigger: 'run' | 'unarchive' | 'archive';
  uuid: number;
}

export interface JobDefinitionsOptionItem extends JobDefinitionsCellOption {
  project_id?: number;
  job_definition_id?: number;
}

export interface JobsCellOption {
  name: string;
  trigger: 'run' | 'edit' | 'stop';
  uuid: number;
}

export interface JobsOptionItem extends JobsCellOption {
  project_id?: number;
  job_id?: number;
  job_definition_id?: number;
}

type ScheduledBatchesCellTrigger = 'run' | 'edit' | 'stop' | 'start' | 'unarchive' | 'archive';
export interface ScheduledBatchesCellOption {
  name: string;
  trigger: ScheduledBatchesCellTrigger;
  uuid: number;
}

export interface ScheduledBatchesOptionItem extends JobDefinitionsCellOption {
  project_id: number;
  batch_definition_id: number;
  scheduled_batch_id: number;
}

export type JobFile = Partial<{
  file_bytes: number;
  uuid: string;
  size_bytes: number;
}>;

export interface JobStandardOutData {
  starting_byte: number;
  ending_byte: number;
}

export interface EditJobData {
  batch_descriptor: string;
  timeout_seconds: number | null;
}

export enum ERROR_LEVEL {
  VALID = 1,
  WARNING = 2,
  ERROR = 3,
  REMOVE = 99,
}
